import { 
	ActionService, 
	AuthService ,
	TaskService
} from 'phoenix/services';
import { 
	Task, 
	TaskAction,
	TaskStatus
} from 'sharebase/domain';
import { TaskFactory } from 'sharebase/factories';
import { TaskSearchFlags } from 'phoenix/domain';

interface TaskPromiseCache {
	openTasksICreated:ng.IPromise<void>,
	openTasksAssignedToMe:ng.IPromise<void>,
	historicalTasks:ng.IPromise<void>
}

export class TasksDataStore {
	public static $inject = [
		'pxActionService',
		'pxAuthService',
		'pxTaskService',
		'sbTaskFactory',
		'$q'
	];

	public constructor(
		pxActionService:ActionService,
		private pxAuthService:AuthService,
		private readonly pxTaskService:TaskService,
		private readonly sbTaskFactory:TaskFactory,
		private readonly $q:ng.IQService
	) {
		pxActionService.subscribe<any>([
			[TaskAction.CreatedTask, (task:Task, event) => this.transitionTask(task)],
			[TaskAction.CompletedTask, (task:Task, event) => this.transitionTask(task)],
			[TaskAction.CancelledTask, (task:Task, event) => this.transitionTask(task)],
			[TaskAction.TaskNotificationReceived, (taskIds:number[], event) => this.handleNewTaskNotifications(taskIds)]
		]);
	}

	private cachedPromises:TaskPromiseCache = {
		historicalTasks: null,
		openTasksAssignedToMe: null,
		openTasksICreated: null
	}

	private _openTasksICreated:Task[] = [];
	public get openTasksICreated() {
		return this._openTasksICreated;
	}

	private _openTasksAssignedToMe:Task[] = [];
	public get openTasksAssignedToMe() {
		return this._openTasksAssignedToMe;
	}

	private _historicalTasks:Task[] = [];
	public get historicalTasks() {
		return this._historicalTasks;
	}

	public getMyTasksForDocument(documentId){
		return this.loadOpenTasksAssignedToMe()
			.then(()=>this._openTasksAssignedToMe.filter(t=>t.targetId === documentId));
	}

	public clearStore() {
		this.clearOpenTasksICreated();
		this.clearOpenTasksAssignedToMe();
		this.clearHistoricalTasks();
	}

	public loadOpenTasksAssignedToMe():ng.IPromise<void> {
		if(!this.cachedPromises.openTasksAssignedToMe) {
			let externalEmail = this.pxAuthService.isShareByLinkUser ? this.getLinkUserEmail() : null;
			this.cachedPromises.openTasksAssignedToMe = this.pxTaskService.getAssignedTasksForUser(TaskSearchFlags.OpenTasks, externalEmail)
				.then(tasks => {
					this._openTasksAssignedToMe = tasks
						.map(phoenixTask => this.sbTaskFactory.create(phoenixTask));
					});
		}

		return this.cachedPromises.openTasksAssignedToMe;
	}

	public loadOpenTasksICreated():ng.IPromise<void> {
		if(!this.cachedPromises.openTasksICreated) {
			this.cachedPromises.openTasksICreated = this.pxTaskService.getCreatedTasksForUser(TaskSearchFlags.OpenTasks)
				.then(tasks => {
					this._openTasksICreated = tasks
						.map(phoenixTask => this.sbTaskFactory.create(phoenixTask));
					});
		}

		return this.cachedPromises.openTasksICreated;
	}

	public loadHistoricalTasks():ng.IPromise<void>{
		if(!this.cachedPromises.historicalTasks) {
			this.cachedPromises.historicalTasks = this.$q.all([
					this.pxTaskService.getAssignedTasksForUser(TaskSearchFlags.CompletedTasks|TaskSearchFlags.CancelledTasks|TaskSearchFlags.ExpiredTasks),
					this.pxTaskService.getCreatedTasksForUser(TaskSearchFlags.CompletedTasks|TaskSearchFlags.CancelledTasks|TaskSearchFlags.ExpiredTasks)
				])
				.then(historicTasks => {
					this._historicalTasks = historicTasks[0].concat(historicTasks[1])
						.map(pxTask => this.sbTaskFactory.create(pxTask));
					});
		}
		
		return this.cachedPromises.historicalTasks;
	}

	public clearHistoricalTasks() {
		this._historicalTasks = [];
		this.cachedPromises.historicalTasks = null;
	}

	private clearOpenTasksAssignedToMe() {
		this._openTasksAssignedToMe = [];
		this.cachedPromises.openTasksAssignedToMe = null;
	}

	private clearOpenTasksICreated() {
		this._openTasksICreated = [];
		this.cachedPromises.openTasksICreated = null;
	}

	private transitionTask(task: Task){
		// remove this task from all collections
		this._openTasksAssignedToMe = this._openTasksAssignedToMe.filter(t => t.taskId !== task.taskId);
		this._openTasksICreated = this.openTasksICreated.filter(t => t.taskId !== task.taskId);
		this._historicalTasks = this._historicalTasks.filter(t => t.taskId !== task.taskId);

		// push to proper collection based on status and assignee/creator
		if (task.status === TaskStatus.Open){
			if(task.assigneeId === this.pxAuthService.userId){
				this._openTasksAssignedToMe.push(task);
			} else if(task.creatorId === this.pxAuthService.userId){
				this._openTasksICreated.push(task);
			}
		} else {
			this._historicalTasks.push(task);
		}
	}

	private handleNewTaskNotifications(taskIds: number[]){
		taskIds.forEach(taskId => {
			this.pxTaskService.getTask(taskId)
				.then(pxTask => {
					let task = this.sbTaskFactory.create(pxTask);
					this.transitionTask(task);
				})
				.catch(() => {
					console.error('Task could not be loaded');
				});
		});
	}
	
	private getLinkUserEmail() {
		let emailAddress = this.pxAuthService.user.EmailAddress;
		// API returns emails as a dash for general link users
		return emailAddress !== '-' ? emailAddress : null;
	}
}


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/tasksDataStore.ts